interface Window {
    finnUserId?: string;
    document: Document;
}

interface Document {
    cookie: string;
}

export type ValidPayload = Device | Actor | Experiments;

interface Tracker {
    update(payload: ValidPayload, merge: boolean): void;
}

interface Device {
    device: {
        additionalDeviceIds: {
            '@id': string;
            component: string;
        }[];
    };
}

interface Actor {
    actor: {
        additionalIds: {
            '@id': string;
            component: string;
        }[];
    };
}

interface Experiment {
    platform: string;
    id: string;
    variant: string;
}

interface Experiments {
    experiments: Experiment[];
}

export class PluginConfig {
    constructor(readonly window: Window) {}
}

export default class FinnPulsePlugin {
    private readonly tracker: Tracker;
    private win: Window;

    constructor(tracker: Tracker, config: PluginConfig) {
        this.tracker = tracker;

        if (config.window) {
            this.win = config.window;
        } else {
            this.win = window;
        }
    }

    injectFinnData(): void {
        this.injectLoginId();
        this.injectAbTestData();
    }

    injectFinnIds(): void {
        this.injectDeviceId();
        this.injectLoginId();
    }

    injectDeviceId(): void {
        const deviceId = this.getCookie('USERID');
        if (deviceId && deviceId !== '') {
            const device: Device = {
                device: {
                    additionalDeviceIds: [
                        {
                            '@id': `sdrn:finn:device:id:${deviceId}`,
                            component: 'finn',
                        },
                    ],
                },
            };
            this.tracker.update(device, true);
        }
    }

    injectLoginId(): void {
        const finnUserId = this.win.finnUserId;
        if (finnUserId && finnUserId !== '') {
            const actor: Actor = {
                actor: {
                    additionalIds: [
                        {
                            '@id': `sdrn:finn.no:user:${finnUserId}`,
                            component: 'finn',
                        },
                    ],
                },
            };
            this.tracker.update(actor, true);
        }
    }

    injectAbTestData(): void {
        const abTestData = this.getCookie('abtest');

        if (!abTestData || abTestData === '' || abTestData.charAt(0) !== '{') {
            return;
        }

        let abTestJson;
        try {
            abTestJson = JSON.parse(abTestData);
        } catch (e) {
            return;
        }

        const experiments: Experiment[] = [];

        Object.keys(abTestJson).forEach(key => {
            // Toggle-variant format: 'toggle_variant'.
            const abTestSplit: string[] = key.split('_');

            if (!abTestSplit || abTestSplit.length !== 2) {
                return; // Continue.
            }

            if (abTestSplit[0] !== '' && abTestSplit[1] !== '') {
                experiments.push({
                    platform: 'finn.no',
                    id: abTestSplit[0],
                    variant: abTestSplit[1],
                });
            }
        });

        if (experiments.length > 0) {
            this.tracker.update({ experiments: experiments }, true);
        }
    }

    private getCookie(key: string): string | null {
        if (!key || !this.win.document.cookie) {
            return null;
        }
        return (
            decodeURIComponent(
                this.win.document.cookie.replace(
                    new RegExp(
                        `(?:(?:^|.*;)\\s*${encodeURIComponent(key).replace(
                            /[-.+*]/g,
                            '\\$&',
                        )}\\s*\\=\\s*([^;]*).*$)|^.*$`,
                    ),
                    '$1',
                ),
            ) || null
        );
    }
}
